/**
 * @fileoverview Play logger for CasebookMap Log Play tab.
 * @module helpers/PlayLogger
 */

import { showStatusMessage, escapeHtml } from '../utils/jrfuncs.js';
import { MAPAPP_DEFAULTS } from '../constants.js';
import { setupTextSearchInput } from '../utils/InputHandlerFactory.js';


const LOGPLAY_TEXTAREA_ID = 'logplay-textarea';
const LOGPLAY_CARET = '> ';


/**
 * PlayLogger manages the Log Play tab functionality.
 * @class
 */
export class PlayLogger {
    /**
     * Creates a new PlayLogger instance.
     * @param {Object} mapApp - The MapApp instance
     */
    constructor(mapApp) {
        this.mapApp = mapApp;
        this.initialized = false;
        this.lastLogHeaderTime = null;
        this.lastLogText = null;
        this.saveStateTimer = null;
        // True while the textarea holds the log of the world the state manager now saves into (set
        // by loadForCurrentWorld, cleared by leaveCurrentWorld); see doSaveState.
        this.worldLogLoaded = false;

        // Search state
        this.searchQuery = '';
        this.matchCount = 0;
        this.currentMatchIndex = -1;
        this.searchCleanup = null;
        this.backdropEl = null;
        // When true, the backdrop shows ONLY lines containing the search text
        // (hiding everything else) instead of highlighting in place.
        this.filterMode = false;
    }


    // ==========================================
    // INITIALIZATION
    // ==========================================

    /**
     * Wires up the play logger's listeners, ONCE per page (they sit on static page DOM, and a world
     * switch re-runs the world load: binding per world stacked the handlers). The log TEXT is per
     * world (user decision, Oct 2026): loadForCurrentWorld() puts the current world's saved log in
     * the textarea, and MapApp calls it on every world load, after this.
     * Call after DOM is ready.
     */
    initialize() {
        if (this.initialized) return;
        this.initialized = true;

        const textarea = this.getTextarea();
        if (!textarea) return;

        // Save on input changes, and refresh highlight backdrop if search is active
        textarea.addEventListener('input', () => {
            this.scheduleSaveState();
            if (this.searchQuery) {
                this.updateHighlightBackdrop();
            }
        });

        // Insert caret prompt after user presses Enter
        textarea.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                const pos = textarea.selectionStart;
                textarea.value = textarea.value.substring(0, pos) + '\n' + LOGPLAY_CARET + textarea.value.substring(pos);
                const newPos = pos + 1 + LOGPLAY_CARET.length;
                textarea.selectionStart = newPos;
                textarea.selectionEnd = newPos;
                textarea.scrollTop = textarea.scrollHeight;
                this.scheduleSaveState();
            }
        });

        // Sync textarea scroll to highlight backdrop
        this.backdropEl = document.getElementById('logplay-highlight-backdrop');
        textarea.addEventListener('scroll', () => {
            if (this.backdropEl) {
                this.backdropEl.scrollTop = textarea.scrollTop;
            }
        });

        // Search header elements
        this.searchHeaderEl = document.getElementById('logplay-search-header');
        this.matchCountEl = document.getElementById('logplay-match-count');

        // Wire up search input
        const searchInputEl = document.getElementById('logplay-search-input');
        const searchClearBtn = document.getElementById('logplay-search-clear');
        if (searchInputEl) {
            this.searchCleanup = setupTextSearchInput(searchInputEl, searchClearBtn, (value, isEnterKey) => {
                if (isEnterKey && this.matchCount > 0 && value === this.searchQuery) {
                    this.goToNextMatch();
                } else {
                    this.performSearch(value);
                }
            });

            // Shift+Enter navigates to previous match
            searchInputEl.addEventListener('keydown', (e) => {
                if (e.key === 'Enter' && e.shiftKey && this.matchCount > 0) {
                    e.preventDefault();
                    this.goToPrevMatch();
                }
            });
        }

        // Wire up prev/next match buttons
        const prevBtn = document.getElementById('logplay-prev-match');
        const nextBtn = document.getElementById('logplay-next-match');
        if (prevBtn) prevBtn.addEventListener('click', () => this.goToPrevMatch());
        if (nextBtn) nextBtn.addEventListener('click', () => this.goToNextMatch());

        // Wire up filter toggle button
        const filterBtn = document.getElementById('logplay-filter-toggle');
        if (filterBtn) filterBtn.addEventListener('click', () => this.toggleFilterMode());
    }


    /**
     * Shows the CURRENT world's saved play log (the log is stored per world). Called by MapApp on
     * every world load. When this text was restored only by the bind-once initialize(), a world
     * switch left the old world's log in the textarea, and the next save wrote it over the new
     * world's own log (second code study 11.1, Oct 2026).
     */
    loadForCurrentWorld() {
        const textarea = this.getTextarea();
        if (!textarea) return;
        textarea.value = this.mapApp.getStateManager().getPlayLogText() || '';
        this.worldLogLoaded = true;
        // the time-header and duplicate-line memory belonged to the previous world's log
        this.lastLogHeaderTime = null;
        this.lastLogText = null;
        if (this.searchQuery) {
            this.updateHighlightBackdrop();
        }
        this.scrollToEnd();
    }


    /**
     * Saves now if a save is waiting (scheduleSaveState's timer).
     */
    flushPendingSave() {
        if (!this.saveStateTimer) return;
        clearTimeout(this.saveStateTimer);
        this.saveStateTimer = null;
        this.doSaveState();
    }


    /**
     * Called by MapApp before a world is torn down, while the state manager still writes to the OLD
     * world: saves a waiting save there (so one timed for the old world's log never lands in the
     * next world's), then empties the textarea, so a next world that fails to load part way does
     * not show -- and on the next keystroke save -- the old world's log as its own.
     */
    leaveCurrentWorld() {
        this.flushPendingSave();
        // Nothing typed into the emptied pane is saved until the next world's log is loaded: the
        // state manager still points at the world being left (or, when the next world throws part
        // way, at that world), and a save from the empty pane replaced that world's whole saved log.
        this.worldLogLoaded = false;
        const textarea = this.getTextarea();
        if (textarea) {
            textarea.value = '';
        }
    }


    // ==========================================
    // ACCESSORS
    // ==========================================

    /**
     * Returns the Log Play textarea element.
     * @returns {HTMLTextAreaElement|null}
     */
    getTextarea() {
        return document.getElementById(LOGPLAY_TEXTAREA_ID);
    }

    /**
     * Scrolls the play log textarea to the bottom.
     */
    scrollToEnd() {
        const textarea = this.getTextarea();
        if (!textarea) return;
        requestAnimationFrame(() => {
            textarea.scrollTop = textarea.scrollHeight;
        });
    }


    // ==========================================
    // TIME LABEL
    // ==========================================

    /**
     * Returns a formatted string with the current local date and time.
     * Format: "Wed, Jan 8, 2025, 3:45 pm"
     * @returns {string}
     */
    calcCurrentTimeLabel() {
        const now = new Date();
        const dayOfWeek = now.toLocaleDateString('en-US', { weekday: 'short' });
        const date = now.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        const time = now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }).toLowerCase();
        return `${dayOfWeek}, ${date}, ${time}`;
    }









    
    // ==========================================
    // LOGGING
    // ==========================================

    /**
     * Appends a line of text to the bottom of the play log textarea and persists it.
     * @param {string} text - The text to append
     */
    addRawTextLineToTextArea(text) {
        const textarea = this.getTextarea();
        if (!textarea) return;
        if (textarea.value && !textarea.value.endsWith('\n')) {
            textarea.value += '\n';
        }
        textarea.value += text + '\n';
        this.scheduleSaveState();
    }

    /**
     * Adds a time header to the play log if enough time has elapsed since the last one.
     * Uses TIME_BETWEEN_PLAY_LOG_TIME_HEADERS_IN_SECONDS from constants.
     */
    addNewTimeHeaderIfAppropriate() {
        const now = new Date();
        if (this.lastLogHeaderTime) {
            const elapsedSeconds = (now - this.lastLogHeaderTime) / 1000;
            if (elapsedSeconds < MAPAPP_DEFAULTS.TIME_BETWEEN_PLAY_LOG_TIME_HEADERS_IN_SECONDS) {
                return;
            }
        }
        this.lastLogHeaderTime = now;
        const separator = '=====';
        const timeLabel = this.calcCurrentTimeLabel();
        this.addRawTextLineToTextArea(`${separator} ${timeLabel} ${separator}`);
    }

    /**
     * Adds a log entry: inserts a time header if appropriate, then appends the text.
     * @param {string} text - The log text to add
     */
    addLogText(text) {
        //if (this.mapApp.getStateManager().getHideLogPanel()) return;
        if (text === this.lastLogText) return;
        this.lastLogText = text;
        this.removeEndCaret();
        this.addNewTimeHeaderIfAppropriate();
        this.addRawTextLineToTextArea(text);
        this.addEndCaret();
        this.scheduleSaveState();
    }

    /**
     * Appends a caret prompt '> ' to the end of the textarea without a trailing newline.
     */
    addEndCaret() {
        const textarea = this.getTextarea();
        if (!textarea) return;
        if (textarea.value && !textarea.value.endsWith('\n')) {
            textarea.value += '\n';
        }
        textarea.value += LOGPLAY_CARET;
        textarea.scrollTop = textarea.scrollHeight;
    }

    /**
     * Removes the caret prompt '> ' from the end of the textarea if present.
     */
    removeEndCaret() {
        const textarea = this.getTextarea();
        if (!textarea) return;
        if (textarea.value.endsWith('\n' + LOGPLAY_CARET)) {
            textarea.value = textarea.value.slice(0, -(LOGPLAY_CARET.length + 1));
        } else if (textarea.value.endsWith(LOGPLAY_CARET)) {
            textarea.value = textarea.value.slice(0, -LOGPLAY_CARET.length);
        }
    }

    scheduleSaveState() {
        if (this.saveStateTimer) {
            clearTimeout(this.saveStateTimer);
        }
        this.saveStateTimer = setTimeout(() => {
            this.saveStateTimer = null;
            this.doSaveState();
        }, MAPAPP_DEFAULTS.SAVE_LOG_STATE_FREQUENCY_SECONDS * 1000);
    }

    doSaveState() {
        if (!this.worldLogLoaded) {
            // no world's log in the pane (between worlds, or after a failed world load)
            return;
        }
        const textarea = this.getTextarea();
        if (textarea) {
            this.mapApp.getStateManager().setPlayLogText(textarea.value);
        }
    }









    // ==========================================
    // SEARCH
    // ==========================================

    /**
     * Performs a search on the textarea content and updates the highlight backdrop.
     * @param {string} query - The search query
     */
    performSearch(query) {
        const trimmed = (query || '').trim();
        this.searchQuery = trimmed;
        this.currentMatchIndex = -1;

        const textarea = this.getTextarea();
        const searchClearBtn = document.getElementById('logplay-search-clear');

        // Update clear button visibility
        if (searchClearBtn) {
            searchClearBtn.classList.toggle('visible', !!trimmed);
        }

        if (!trimmed || !textarea) {
            this.matchCount = 0;
            this.clearHighlightBackdrop();
            return;
        }

        this.updateHighlightBackdrop();

        // Navigate to first match
        if (this.matchCount > 0) {
            this.currentMatchIndex = 0;
            this.updateHighlightBackdrop();
            this.scrollToCurrentMatch(textarea);
        }
        this.updateSearchHeader();
    }

    /**
     * Updates the highlight backdrop div with marked content matching the search query.
     */
    updateHighlightBackdrop() {
        const textarea = this.getTextarea();
        if (!this.backdropEl || !textarea) return;

        const query = this.searchQuery;

        if (!query) {
            this.clearHighlightBackdrop();
            return;
        }

        const lowerQuery = query.toLowerCase();

        // In filter mode the backdrop becomes the visible layer and shows only
        // the lines that contain the query; otherwise it mirrors the full
        // textarea text and just paints highlight backgrounds behind it. The
        // textarea's value is never altered, so the log data is untouched.
        const fullText = textarea.value;
        const text = this.filterMode
            ? fullText.split('\n').filter(line => line.toLowerCase().includes(lowerQuery)).join('\n')
            : fullText;

        // Find all match positions in the (possibly filtered) display text
        const lowerText = text.toLowerCase();
        const matchPositions = [];
        let searchFrom = 0;
        while (true) {
            const idx = lowerText.indexOf(lowerQuery, searchFrom);
            if (idx === -1) break;
            matchPositions.push(idx);
            // continue AFTER the match, so matches never overlap: an overlapping one ("**" in "*****")
            // started before the previous mark ended, and the html below then repeated characters
            // and slid every later highlight off its text
            searchFrom = idx + lowerQuery.length;
        }

        this.matchCount = matchPositions.length;

        // Build HTML with <mark> tags around matches
        let html = '';
        let lastEnd = 0;
        for (let i = 0; i < matchPositions.length; i++) {
            const start = matchPositions[i];
            const end = start + query.length;
            html += escapeHtml(text.substring(lastEnd, start));
            const cssClass = (i === this.currentMatchIndex) ? 'current' : '';
            html += `<mark class="${cssClass}">` + escapeHtml(text.substring(start, end)) + '</mark>';
            lastEnd = end;
        }
        html += escapeHtml(text.substring(lastEnd));

        // Append a trailing newline so the backdrop height matches the textarea
        // (textarea always has an implicit trailing line for the cursor)
        html += '\n';

        this.backdropEl.innerHTML = html;
        textarea.classList.add('search-active');
        textarea.classList.toggle('filter-active', this.filterMode);
        this.backdropEl.classList.toggle('filter-active', this.filterMode);

        // In filter mode the backdrop scrolls independently (its content differs
        // from the textarea); otherwise keep it aligned to the textarea scroll.
        if (!this.filterMode) {
            this.backdropEl.scrollTop = textarea.scrollTop;
        }
    }

    /**
     * Clears the highlight backdrop and removes transparent textarea background.
     */
    clearHighlightBackdrop() {
        if (this.backdropEl) {
            this.backdropEl.innerHTML = '';
            this.backdropEl.classList.remove('filter-active');
        }
        const textarea = this.getTextarea();
        if (textarea) {
            textarea.classList.remove('search-active');
            textarea.classList.remove('filter-active');
        }
        this.matchCount = 0;
        this.currentMatchIndex = -1;
        this.updateSearchHeader();
    }

    /**
     * Toggles filter mode. When enabled, the play log shows only the lines that
     * contain the current search text (everything else is hidden); when
     * disabled, all lines are shown with the matches highlighted in place.
     * The filter state is sticky across searches (the button stays armed).
     */
    toggleFilterMode() {
        this.filterMode = !this.filterMode;

        // Update the toggle button's appearance
        const filterBtn = document.getElementById('logplay-filter-toggle');
        if (filterBtn) {
            filterBtn.classList.toggle('active', this.filterMode);
            filterBtn.setAttribute('aria-pressed', this.filterMode ? 'true' : 'false');
            const icon = filterBtn.querySelector('i');
            if (icon) {
                icon.className = this.filterMode ? 'bi bi-funnel-fill' : 'bi bi-funnel';
            }
        }

        // Re-render for the new mode and snap to the first match for a clean view.
        this.currentMatchIndex = -1;
        this.updateHighlightBackdrop();
        if (this.matchCount > 0) {
            this.currentMatchIndex = 0;
            this.updateHighlightBackdrop();
            this.scrollToCurrentMatch(this.getTextarea());
        }
        this.updateSearchHeader();
    }

    /**
     * Updates the search header with the current match count and position.
     */
    updateSearchHeader() {
        if (!this.searchHeaderEl || !this.matchCountEl) return;

        if (this.matchCount > 0) {
            this.searchHeaderEl.style.display = 'flex';
            // Append '*' while filtering to flag that non-matching lines are hidden.
            const filterMark = this.filterMode ? '*' : '';
            this.matchCountEl.textContent = `${this.currentMatchIndex + 1} of ${this.matchCount} match${this.matchCount !== 1 ? 'es' : ''}${filterMark}`;
        } else if (this.searchQuery) {
            this.searchHeaderEl.style.display = 'flex';
            this.matchCountEl.textContent = 'No matches';
        } else {
            this.searchHeaderEl.style.display = 'none';
            this.matchCountEl.textContent = '';
        }
    }

    /**
     * Navigates to the next search match (wraps around).
     */
    goToNextMatch() {
        if (this.matchCount === 0) return;
        this.currentMatchIndex = (this.currentMatchIndex + 1) % this.matchCount;
        this.updateHighlightBackdrop();
        this.updateSearchHeader();
        this.scrollToCurrentMatch(this.getTextarea());
    }

    /**
     * Navigates to the previous search match (wraps around).
     */
    goToPrevMatch() {
        if (this.matchCount === 0) return;
        this.currentMatchIndex = (this.currentMatchIndex - 1 + this.matchCount) % this.matchCount;
        this.updateHighlightBackdrop();
        this.updateSearchHeader();
        this.scrollToCurrentMatch(this.getTextarea());
    }

    /**
     * Scrolls the textarea to show the current match.
     * Uses the character position to estimate the scroll offset.
     * @param {HTMLTextAreaElement} textarea
     */
    scrollToCurrentMatch(textarea) {
        if (!textarea || !this.backdropEl || this.currentMatchIndex < 0) return;

        const markEls = this.backdropEl.querySelectorAll('mark');
        const currentMark = markEls[this.currentMatchIndex];
        if (!currentMark) return;

        // Scroll the backdrop to center the current mark, then sync textarea
        const markTop = currentMark.offsetTop;
        const containerHeight = textarea.clientHeight;
        const scrollTarget = Math.max(0, markTop - containerHeight / 3);
        if (this.filterMode) {
            // In filter mode the backdrop is the visible scroller; the textarea
            // is hidden underneath and its content/scroll no longer align.
            this.backdropEl.scrollTop = scrollTarget;
        } else {
            textarea.scrollTop = scrollTarget;
            this.backdropEl.scrollTop = scrollTarget;
        }
    }

    // ==========================================
    // ACTIONS
    // ==========================================

    /**
     * Clears the play log textarea after user confirmation.
     */
    clearPlayLog() {
        const textarea = this.getTextarea();
        if (!textarea) return;
        if (!textarea.value) return;
        // Defer confirm() so Bootstrap dropdown can close first;
        // a synchronous confirm() blocks the event loop and prevents
        // the dropdown from dismissing, which can cause the dialog
        // to reappear when focus returns.
        if (textarea.value.length >= 50) {
            setTimeout(() => {
                if (!confirm('Are you sure you want to completely clear the play log?')) return;
                this.doClearPlayLog(textarea);
            }, 0);
        } else {
            this.doClearPlayLog(textarea);
        }
    }

    /**
     * Performs the actual play log clearing.
     * @param {HTMLTextAreaElement} textarea
     * @private
     */
    doClearPlayLog(textarea) {
        textarea.value = '';
        this.clearHighlightBackdrop();
        this.addEndCaret();
        this.scheduleSaveState();
    }

    /**
     * Copies the play log textarea contents to the clipboard and shows a toast.
     */
    async copyPlayLogToClipboard() {
        const textarea = this.getTextarea();
        if (!textarea) return;
        try {
            await navigator.clipboard.writeText(textarea.value);
            showStatusMessage('Play log copied to clipboard.', 'text-bg-success', 'bi bi-clipboard-check');
        } catch (error) {
            showStatusMessage('Failed to copy to clipboard.', 'text-bg-danger', 'bi bi-exclamation-triangle-fill');
        }
    }

    /**
     * Copies all lines starting with * or > from the play log to the clipboard.
     */
    async copyStarLinesToClipboard() {
        const textarea = this.getTextarea();
        if (!textarea) return;
        const starLines = textarea.value.split('\n').filter(line => {
            const trimmed = line.trimStart();
            return ((trimmed.startsWith('*') || trimmed.startsWith('>')) && trimmed!='> ');
        });
        if (starLines.length === 0) {
            showStatusMessage('No lines starting with * or > found.', 'text-bg-warning', 'bi bi-info-circle');
            return;
        }
        try {
            await navigator.clipboard.writeText(starLines.join('\n'));
            showStatusMessage(`${starLines.length} line${starLines.length !== 1 ? 's' : ''} copied to clipboard.`, 'text-bg-success', 'bi bi-clipboard-check');
        } catch (error) {
            showStatusMessage('Failed to copy to clipboard.', 'text-bg-danger', 'bi bi-exclamation-triangle-fill');
        }
    }


    async saveLogInteratively() {
        const textarea = this.getTextarea();
        if (!textarea || !textarea.value) {
            showStatusMessage('Play log is empty.', 'text-bg-warning', 'bi bi-info-circle');
            return;
        }

        const text = textarea.value;
        const now = new Date();
        const timestamp = now.getFullYear()
            + String(now.getMonth() + 1).padStart(2, '0')
            + String(now.getDate()).padStart(2, '0')
            + '-' + String(now.getHours()).padStart(2, '0')
            + String(now.getMinutes()).padStart(2, '0')
            + String(now.getSeconds()).padStart(2, '0');
        const filename = `playlog_${timestamp}.txt`;

        // Use File System Access API if available (Chromium browsers)
        if (window.showSaveFilePicker) {
            try {
                const handle = await window.showSaveFilePicker({
                    suggestedName: filename,
                    types: [{
                        description: 'Text Files',
                        accept: { 'text/plain': ['.txt'] },
                    }],
                });
                const writable = await handle.createWritable();
                await writable.write(text);
                await writable.close();
                showStatusMessage('Play log saved.', 'text-bg-success', 'bi bi-save');
            } catch (error) {
                if (error.name !== 'AbortError') {
                    showStatusMessage('Failed to save play log.', 'text-bg-danger', 'bi bi-exclamation-triangle-fill');
                }
            }
        } else {
            // Fallback: trigger a download
            const blob = new Blob([text], { type: 'text/plain' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            showStatusMessage('Play log downloaded.', 'text-bg-success', 'bi bi-save');
        }
    }
}
